'use client';

import { useEffect, useRef, useState } from 'react';

import type { BioContext } from '@ps/functions/bio/bioContext';
import { bioUrl } from '@ps/functions/bio/bioUrls';
import { isIframeMode } from '@ps/functions/bio/iframeMode';
import { useCurrentUserId } from '@ps/ui/contexts/auth';
import { Check, Copy, Eye, Pencil } from 'lucide-react';

interface Props {
  bioContext: BioContext;
}

const COPIED_RESET_MS = 1000;
// px-3 padding on both sides plus the 1px border.
const PILL_CHROME_PX = 26;

/**
 * Owner-only preview banner on the VISITOR bio tree — mirrors the editor's
 * preview toolbar (`my-popstore/bio/toolbar/ToolbarPreviewMode`):
 *
 *   [👁 You're previewing <domain>/<url> ⧉]                         [✏ Edit]
 *
 * When the signed-in user is the creator viewing their own public bio, it pins
 * to the top of the bio and offers a jump back to the editor. It renders nothing
 * for everyone else, so a regular visitor's layout is untouched.
 *
 * The Edit action HARD-navigates via `<a>`: `/my-popstore` lives in the
 * `(dashboard)` shell, a different tree from the visitor bio, so it needs a full
 * document load, not a client transition inside the bio route.
 *
 * Both the displayed and copied URLs come from {@link bioUrl}`(ctx,{share:true})`
 * — the creator's canonical absolute permalink, derived from the context so it's
 * correct on an independent custom domain too.
 */
export function ToolbarPreviewMode({ bioContext }: Props) {
  const currentUserId = useCurrentUserId();
  const [copied, setCopied] = useState(false);
  const [showLabel, setShowLabel] = useState(true);
  const slotRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const labelRef = useRef<HTMLSpanElement>(null);
  const fullWidthRef = useRef(0);

  // Show the label only when the whole pill fits, so the pill hugs the url when it is hidden.
  useEffect(() => {
    const slot = slotRef.current;
    const content = contentRef.current;
    if (!slot || !content) return;
    const update = () => {
      if (labelRef.current) fullWidthRef.current = content.offsetWidth;
      setShowLabel(fullWidthRef.current + PILL_CHROME_PX <= slot.clientWidth);
    };
    const observer = new ResizeObserver(update);
    observer.observe(slot);
    return () => observer.disconnect();
  }, [currentUserId, bioContext]);

  if (currentUserId !== bioContext.creatorId) return null;
  // Embedded preview (`?source=iframe`): the phone mockup is already framed as
  // a preview by its host, so the banner is redundant chrome — and its Edit
  // link would hard-navigate the dashboard inside the little iframe. Read after
  // the owner gate so it never runs on the SSR/hydration path.
  if (isIframeMode()) return null;

  const shareUrl = bioUrl(bioContext, { share: true }).home();
  const displayUrl = shareUrl.replace(/^https?:\/\//, '');

  function onCopy() {
    void navigator.clipboard?.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), COPIED_RESET_MS);
  }

  return (
    <div
      className="sticky top-0 z-30 flex w-full items-center gap-2 border-b border-white/10 px-3 py-2 backdrop-blur-md"
      style={{ backgroundColor: 'rgba(10, 10, 15, 0.55)' }}
    >
      <div ref={slotRef} className="flex min-w-0 flex-1">
        <div className="flex min-w-0 max-w-full items-center overflow-hidden rounded-full border border-white/30 bg-black/40 px-3 py-1 text-xs font-medium text-white sm:flex-1">
          <div
            ref={contentRef}
            className="flex items-center gap-2 whitespace-nowrap"
          >
            <Eye className="h-3.5 w-3.5 shrink-0" aria-hidden />
            {showLabel && <span ref={labelRef}>You&rsquo;re previewing</span>}
            <button
              type="button"
              onClick={onCopy}
              className="border-mint-green-300 text-mint-green-300 inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 transition active:opacity-85"
            >
              {displayUrl}
              {copied ? (
                <Check className="h-3 w-3 shrink-0" aria-hidden />
              ) : (
                <Copy className="h-3 w-3 shrink-0" aria-hidden />
              )}
            </button>
          </div>
        </div>
      </div>
      <a
        href="/my-popstore"
        className="bg-mint-green-300 hover:bg-mint-green-400 flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold text-black transition"
      >
        <Pencil className="h-3.5 w-3.5" aria-hidden />
        Edit
      </a>
    </div>
  );
}
